import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './ChatInterno.module.css';
import { useChatInterno, ChatInternoConversa, ChatInternoMensagem } from './useChatInterno';
import { useAuth } from '../../lib/useAuth';
import Icon from '../Icon';
import { mdiClose, mdiPlus, mdiSend, mdiArrowLeft, mdiAccountPlus, mdiMessageText } from '@mdi/js';
import CampoMemoMencao from '../Formulario/CampoMemoMencao';
import { CampoSeletorOption, CampoSeletorOptions } from '../Formulario/CampoSeletor';
import Botao from '../Botao';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery } from 'react-query';
import { api } from '../../lib/apiCall';

type View = 'lista' | 'conversa' | 'nova' | 'adicionar-participante';

export default function ChatInterno() {
  const { user } = useAuth();

  // Verifica se o chat interno está habilitado
  const { data: configData, isLoading: isLoadingConfig } = useQuery(
    'configuracoes/chat-interno/habilitado',
    async () => {
      const response = await api.get('/api/configuracoes/chat-interno/habilitado');
      return response.data.result;
    },
    {
      enabled: !!user?.id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  );

  const chatInternoHabilitado = configData?.habilitado ?? true;

  const {
    conversas,
    conversaSelecionada,
    mensagens,
    loading,
    loadingMensagens,
    totalNaoLidas,
    usuariosDigitando,
    usuarios,
    selecionarConversa,
    enviarMensagem,
    criarConversa,
    adicionarParticipante,
    setDigitando,
    fecharConversa,
    carregarMaisMensagens,
    temMaisMensagens,
  } = useChatInterno();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('lista');
  const [mensagemTexto, setMensagemTexto] = useState('');
  const [participantesSelecionados, setParticipantesSelecionados] = useState<number[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 30, y: 120 });
  const [usuarioSearch, setUsuarioSearch] = useState('');

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const mensagensEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const digitandoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll para última mensagem
  useEffect(() => {
    if (mensagensEndRef.current) {
      mensagensEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mensagens]);

  // Reset view quando fecha
  useEffect(() => {
    if (!isOpen) {
      setView('lista');
      fecharConversa();
    }
  }, [isOpen, fecharConversa]);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Previne o drag nativo da imagem
    if (buttonRef.current) {
      dragStartRef.current = {
        x: e.clientX - buttonRef.current.getBoundingClientRect().left,
        y: e.clientY - buttonRef.current.getBoundingClientRect().top,
      };
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current) return;

    setIsDragging(true);

    const newX = window.innerWidth - e.clientX - 56 + dragStartRef.current.x;
    const newY = window.innerHeight - e.clientY - 56 + dragStartRef.current.y;

    // Limita dentro da viewport
    const clampedX = Math.max(10, Math.min(window.innerWidth - 66, newX));
    const clampedY = Math.max(10, Math.min(window.innerHeight - 66, newY));

    setButtonPosition({ x: clampedX, y: clampedY });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Se estava arrastando, não abre o chat
      setTimeout(() => setIsDragging(false), 10);
    }
    dragStartRef.current = null;
  }, [isDragging]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Formata data da mensagem
  const formatarData = (dataStr: string) => {
    const data = new Date(dataStr);
    if (isToday(data)) {
      return format(data, 'HH:mm');
    }
    if (isYesterday(data)) {
      return `Ontem ${format(data, 'HH:mm')}`;
    }
    return format(data, 'dd/MM HH:mm', { locale: ptBR });
  };

  // Formata conteúdo com menções
  const formatarConteudo = (conteudo: string) => {
    // Converte <@id|nome> para @nome com destaque
    return conteudo.replace(/<@(\d+)\|([^>]+)>/g, (_, id, nome) => `@${nome}`);
  };

  // Obtém nome do participante para exibição
  const getNomeConversa = (conversa: ChatInternoConversa) => {
    if (conversa.titulo) return conversa.titulo;

    const outrosParticipantes = conversa.participantes?.filter(
      p => p.usuario_id !== user?.id
    );

    if (!outrosParticipantes?.length) return 'Conversa';

    if (outrosParticipantes.length === 1) {
      return outrosParticipantes[0].usuario?.nome || 'Participante';
    }

    return outrosParticipantes
      .slice(0, 2)
      .map(p => p.usuario?.nome?.split(' ')[0])
      .join(', ') + (outrosParticipantes.length > 2 ? '...' : '');
  };

  // Handle envio de mensagem
  const handleEnviar = () => {
    if (!mensagemTexto.trim()) return;

    enviarMensagem(mensagemTexto);
    setMensagemTexto('');
    setDigitando(false);

    if (digitandoTimeoutRef.current) {
      clearTimeout(digitandoTimeoutRef.current);
    }
  };

  // Handle mudança no texto
  const handleTextChange = (valor: string) => {
    setMensagemTexto(valor);

    // Debounce do status de digitação
    if (digitandoTimeoutRef.current) {
      clearTimeout(digitandoTimeoutRef.current);
    }

    setDigitando(true);

    digitandoTimeoutRef.current = setTimeout(() => {
      setDigitando(false);
    }, 2000);
  };

  // Handle seleção de conversa
  const handleSelecionarConversa = (conversa: ChatInternoConversa) => {
    selecionarConversa(conversa);
    setView('conversa');
  };

  // Handle criação de nova conversa
  const handleCriarConversa = async () => {
    if (participantesSelecionados.length === 0) return;

    const novaConversa = await criarConversa(participantesSelecionados);
    if (novaConversa) {
      selecionarConversa(novaConversa);
      setView('conversa');
      setParticipantesSelecionados([]);
    }
  };

  // Toggle seleção de participante
  const toggleParticipante = (usuarioId: number) => {
    setParticipantesSelecionados(prev =>
      prev.includes(usuarioId)
        ? prev.filter(id => id !== usuarioId)
        : [...prev, usuarioId]
    );
  };

  // Usuários digitando na conversa atual
  const digitandoNaConversa = Array.from(usuariosDigitando.values())
    .filter(d => d.chatId === conversaSelecionada?.id)
    .map(d => d.usuario.nome);

  // Opções de usuários para o seletor (agrupados por grupo)
  const usuariosOptions = useMemo((): CampoSeletorOption[] => {
    const opcoesMap = new Map<string, CampoSeletorOption>();

    usuarios
      .filter(u => Number(u.id) !== user?.id)
      .forEach(u => {
        if (u.grupos && u.grupos.length > 0) {
          // Adiciona o usuário em cada grupo que ele pertence
          u.grupos.forEach(g => {
            const key = `${u.id}-${g.id}`;
            opcoesMap.set(key, {
              id: u.id.toString(),
              label: u.nome,
              group: g.nome,
              groupId: g.id.toString(),
            });
          });
        } else {
          // Usuários sem grupo ficam em "Sem grupo"
          opcoesMap.set(`${u.id}-0`, {
            id: u.id.toString(),
            label: u.nome,
            group: 'Sem grupo',
            groupId: '0',
          });
        }
      });

    return Array.from(opcoesMap.values()).sort((a, b) =>
      `${a.group}--${a.label}`.localeCompare(`${b.group}--${b.label}`)
    );
  }, [usuarios, user?.id]);

  // Opções disponíveis para adicionar participantes (exclui os já participantes)
  const usuariosParaAdicionarOptions = useMemo((): CampoSeletorOption[] => {
    if (!conversaSelecionada) return [];

    const participantesIds = new Set(
      conversaSelecionada.participantes?.map(p => p.usuario_id) || []
    );

    return usuariosOptions.filter(opt => !participantesIds.has(Number(opt.id)));
  }, [usuariosOptions, conversaSelecionada]);

  const handleButtonClick = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  };

  // Não renderiza se o chat interno estiver desabilitado ou ainda carregando
  if (isLoadingConfig || !chatInternoHabilitado) {
    return null;
  }

  return (
    <>
      {/* Botão flutuante */}
      <button
        ref={buttonRef}
        className={`${styles.chatInternoButton} ${isDragging ? styles.dragging : ''}`}
        style={{
          right: buttonPosition.x,
          bottom: buttonPosition.y,
        }}
        onMouseDown={handleMouseDown}
        onClick={handleButtonClick}
        title="Chat Interno"
      >
        <img src="/logo.png" alt="Chat Interno" draggable={false} />
        {totalNaoLidas > 0 && (
          <span className={styles.badge}>
            {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
          </span>
        )}
      </button>

      {/* Popup do chat */}
      {isOpen && (
        <div
          className={styles.chatPopup}
          style={{
            right: buttonPosition.x,
            bottom: buttonPosition.y + 70,
          }}
        >
          {/* Header */}
          <div className={styles.header}>
            <span className={styles.headerTitle}>
              {view === 'lista' && 'Chat Interno'}
              {view === 'conversa' && getNomeConversa(conversaSelecionada!)}
              {view === 'nova' && 'Nova Conversa'}
              {view === 'adicionar-participante' && 'Adicionar Participante'}
            </span>
            <div className={styles.headerActions}>
              {view === 'lista' && (
                <button
                  className={styles.headerButton}
                  onClick={() => {
                    setUsuarioSearch('');
                    setView('nova');
                  }}
                  title="Nova conversa"
                >
                  <Icon path={mdiPlus} size={1} color="white" />
                </button>
              )}
              {view === 'conversa' && (
                <button
                  className={styles.headerButton}
                  onClick={() => {
                    setParticipantesSelecionados([]);
                    setUsuarioSearch('');
                    setView('adicionar-participante');
                  }}
                  title="Adicionar participante"
                >
                  <Icon path={mdiAccountPlus} size={1} color="white" />
                </button>
              )}
              <button
                className={styles.headerButton}
                onClick={() => setIsOpen(false)}
                title="Fechar"
              >
                <Icon path={mdiClose} size={1} color="white" />
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className={styles.content}>
            {/* View: Lista de conversas */}
            {view === 'lista' && (
              <>
                {loading ? (
                  <div className={styles.loading}>Carregando...</div>
                ) : conversas.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Icon path={mdiMessageText} size={3} color="var(--tc-color-gray-300)" />
                    <div className={styles.emptyStateTitle}>Nenhuma conversa</div>
                    <div className={styles.emptyStateText}>
                      Clique em + para iniciar uma nova conversa
                    </div>
                  </div>
                ) : (
                  <div className={styles.conversaList}>
                    {conversas.map(conversa => (
                      <div
                        key={conversa.id}
                        className={styles.conversaItem}
                        onClick={() => handleSelecionarConversa(conversa)}
                      >
                        <div className={styles.conversaAvatar}>
                          {getNomeConversa(conversa).charAt(0).toUpperCase()}
                        </div>
                        <div className={styles.conversaInfo}>
                          <div className={styles.conversaNome}>
                            {getNomeConversa(conversa)}
                          </div>
                          {conversa.ultima_mensagem && (
                            <div className={styles.conversaPreview}>
                              {formatarConteudo(conversa.ultima_mensagem.conteudo)}
                            </div>
                          )}
                        </div>
                        <div className={styles.conversaMeta}>
                          {conversa.ultima_mensagem && (
                            <span className={styles.conversaData}>
                              {formatarData(conversa.ultima_mensagem.data_criacao)}
                            </span>
                          )}
                          {(conversa.nao_lidas ?? 0) > 0 && (
                            <span className={styles.conversaBadge}>
                              {conversa.nao_lidas}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* View: Conversa */}
            {view === 'conversa' && conversaSelecionada && (
              <>
                <div className={styles.conversaHeader}>
                  <button
                    className={styles.backButton}
                    onClick={() => {
                      fecharConversa();
                      setView('lista');
                    }}
                  >
                    <Icon path={mdiArrowLeft} size={1} />
                  </button>
                  <div className={styles.conversaHeaderInfo}>
                    <div className={styles.conversaHeaderNome}>
                      {getNomeConversa(conversaSelecionada)}
                    </div>
                    <div className={styles.conversaHeaderParticipantes}>
                      {conversaSelecionada.participantes
                        ?.map(p => p.usuario?.nome)
                        .join(', ')}
                    </div>
                  </div>
                </div>

                <div className={styles.mensagemList}>
                  {temMaisMensagens && (
                    <div className={styles.loadMore}>
                      <button
                        className={styles.loadMoreButton}
                        onClick={carregarMaisMensagens}
                        disabled={loadingMensagens}
                      >
                        {loadingMensagens ? 'Carregando...' : 'Carregar mensagens anteriores'}
                      </button>
                    </div>
                  )}

                  {mensagens.map(mensagem => (
                    <div
                      key={mensagem.id}
                      className={`${styles.mensagemItem} ${mensagem.autor_id === user?.id ? styles.propria : styles.outra
                        }`}
                    >
                      {mensagem.autor_id !== user?.id && (
                        <div className={styles.mensagemAutor}>
                          {mensagem.autor?.nome}
                        </div>
                      )}
                      <div className={styles.mensagemConteudo}>
                        {formatarConteudo(mensagem.conteudo)}
                      </div>
                      <div className={styles.mensagemData}>
                        {formatarData(mensagem.data_criacao)}
                      </div>
                    </div>
                  ))}

                  <div ref={mensagensEndRef} />
                </div>

                {digitandoNaConversa.length > 0 && (
                  <div className={styles.digitando}>
                    {digitandoNaConversa.join(', ')} está digitando...
                  </div>
                )}

                <div className={styles.footer}>
                  <div className={styles.inputContainer}>
                    <CampoMemoMencao
                      value={mensagemTexto}
                      onChange={handleTextChange}
                      placeholder="Digite sua mensagem..."
                      noBorder
                      rows={1}
                      usuarios={usuarios}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleEnviar();
                        }
                      }}
                    />
                    <button
                      className={styles.sendButton}
                      onClick={handleEnviar}
                      disabled={!mensagemTexto.trim()}
                    >
                      <Icon path={mdiSend} size={0.9} color="white" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* View: Nova conversa */}
            {view === 'nova' && (
              <div className={styles.novaConversaForm}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <label style={{ marginBottom: '8px', flexShrink: 0 }}>Selecione os participantes</label>

                  {/* Chips dos selecionados */}
                  {participantesSelecionados.length > 0 && (
                    <div className={styles.participantesLista} style={{ marginBottom: '8px', flexShrink: 0 }}>
                      {participantesSelecionados.map(id => {
                        const usuario = usuarios.find(u => Number(u.id) === id);
                        return (
                          <div
                            key={id}
                            className={`${styles.participanteChip} ${styles.selected}`}
                            onClick={() => toggleParticipante(id)}
                          >
                            {usuario?.nome} ×
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Seletor de usuários por grupo */}
                  <div
                    style={{
                      flex: 1,
                      border: '1px solid var(--tc-color-gray-300)',
                      borderRadius: '8px',
                      padding: '12px',
                      overflow: 'auto',
                      minHeight: 0,
                    }}
                  >
                    <CampoSeletorOptions
                      value={usuariosOptions}
                      selectedId={null}
                      selectedGroupId={null}
                      onSelect={(value) => {
                        if (value) {
                          toggleParticipante(Number(value));
                        }
                      }}
                      onGroupSelect={() => {
                        // Não permite selecionar grupo, apenas usuários
                      }}
                      search={usuarioSearch}
                      onChangeSearch={setUsuarioSearch}
                    />
                  </div>
                </div>

                <div className={styles.novaConversaActions}>
                  <Botao
                    variant="outline-secondary"
                    onClick={() => {
                      setView('lista');
                      setParticipantesSelecionados([]);
                      setUsuarioSearch('');
                    }}
                  >
                    Cancelar
                  </Botao>
                  <Botao
                    variant="primary"
                    onClick={handleCriarConversa}
                    disabled={participantesSelecionados.length === 0}
                  >
                    Iniciar Conversa
                  </Botao>
                </div>
              </div>
            )}

            {/* View: Adicionar participante */}
            {view === 'adicionar-participante' && conversaSelecionada && (
              <div className={styles.novaConversaForm}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <label style={{ marginBottom: '8px', flexShrink: 0 }}>Selecione os participantes para adicionar</label>

                  {/* Chips dos selecionados */}
                  {participantesSelecionados.length > 0 && (
                    <div className={styles.participantesLista} style={{ marginBottom: '8px', flexShrink: 0 }}>
                      {participantesSelecionados.map(id => {
                        const usuario = usuarios.find(u => Number(u.id) === id);
                        return (
                          <div
                            key={id}
                            className={`${styles.participanteChip} ${styles.selected}`}
                            onClick={() => toggleParticipante(id)}
                          >
                            {usuario?.nome} ×
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Seletor de usuários por grupo */}
                  {usuariosParaAdicionarOptions.length > 0 ? (
                    <div
                      style={{
                        flex: 1,
                        border: '1px solid var(--tc-color-gray-300)',
                        borderRadius: '8px',
                        padding: '12px',
                        overflow: 'auto',
                        minHeight: 0,
                      }}
                    >
                      <CampoSeletorOptions
                        value={usuariosParaAdicionarOptions}
                        selectedId={null}
                        selectedGroupId={null}
                        onSelect={(value) => {
                          if (value) {
                            toggleParticipante(Number(value));
                          }
                        }}
                        onGroupSelect={() => {
                          // Não permite selecionar grupo, apenas usuários
                        }}
                        search={usuarioSearch}
                        onChangeSearch={setUsuarioSearch}
                      />
                    </div>
                  ) : (
                    <div className={styles.emptyStateText} style={{ marginTop: '16px', textAlign: 'center' }}>
                      Todos os usuários já são participantes desta conversa.
                    </div>
                  )}
                </div>

                <div className={styles.novaConversaActions}>
                  <Botao
                    variant="outline-secondary"
                    onClick={() => {
                      setView('conversa');
                      setParticipantesSelecionados([]);
                      setUsuarioSearch('');
                    }}
                  >
                    Cancelar
                  </Botao>
                  <Botao
                    variant="primary"
                    onClick={() => {
                      participantesSelecionados.forEach(id => {
                        adicionarParticipante(id);
                      });
                      setParticipantesSelecionados([]);
                      setUsuarioSearch('');
                      setView('conversa');
                    }}
                    disabled={participantesSelecionados.length === 0}
                  >
                    Adicionar
                  </Botao>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
